import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/App';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, X, LogOut, LayoutDashboard, Gift, Globe, ShoppingBag, Coins, User } from 'lucide-react';

const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/gift-cards', label: t('nav.gift_cards') },
    { to: '/credits', label: 'Crédits', icon: Coins, auth: true },
    { to: 'https://www.riding-watt.com/', label: 'Shop', external: true },
  ];

  const toggleLang = () => setLang(lang === 'fr' ? 'en' : 'fr');

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/90 border-b border-[#1A1A1A]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img src={FAVICON_URL} alt="Riding Watt" className="w-9 h-9 rounded-lg" />
            <span className="font-heading text-lg font-bold text-white tracking-tight hidden sm:block">
              RIDING<span className="text-[#00FF00]">WATT</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.filter(link => !link.auth || user).map((link) =>
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium transition-colors hover:text-[#00FF00] text-slate-400 flex items-center gap-1"
                  data-testid="nav-link-shop"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-sm font-medium transition-colors hover:text-[#00FF00] ${
                    isActive(link.to) ? 'text-[#00FF00]' : 'text-slate-400'
                  }`}
                  data-testid={`nav-link-${link.to.replace(/\//g, '') || 'home'}`}
                >
                  {link.label}
                </Link>
              )
            )}

            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-[#00FF00] transition-colors"
              data-testid="lang-toggle"
            >
              <Globe className="w-4 h-4" />
              <span className="uppercase font-bold">{lang === 'fr' ? 'EN' : 'FR'}</span>
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 rounded-full text-white hover:bg-[#1A1A1A]" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#00FF00] flex items-center justify-center">
                      <span className="text-black text-xs font-bold">{user.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="text-sm font-medium text-slate-300 hidden lg:block">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-[#1A1A1A] border-[#2A2A2A]">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} className="text-slate-300 focus:bg-[#2A2A2A] focus:text-white" data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {t('nav.my_bookings')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="text-slate-300 focus:bg-[#2A2A2A] focus:text-white" data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Mon profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/gift-cards')} className="text-slate-300 focus:bg-[#2A2A2A] focus:text-white" data-testid="menu-gift-cards">
                    <Gift className="mr-2 h-4 w-4" />
                    {t('nav.gift_cards')}
                  </DropdownMenuItem>
                  {(user.role === 'super_admin' || user.role === 'franchise_admin') && (
                    <>
                      <DropdownMenuSeparator className="bg-[#2A2A2A]" />
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="text-slate-300 focus:bg-[#2A2A2A] focus:text-white" data-testid="menu-admin">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {t('nav.admin')}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-[#2A2A2A]" />
                  <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="text-red-400 focus:bg-[#2A2A2A] focus:text-red-300" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="btn-pill bg-[#00FF00] text-black hover:bg-[#00CC00] shadow-[0_0_20px_rgba(0,255,0,0.3)] font-bold"
                data-testid="login-button"
              >
                {t('nav.login')}
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLang}
              className="p-2 text-slate-400 hover:text-[#00FF00]"
              data-testid="lang-toggle-mobile"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              className="p-2 text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 border-t border-[#1A1A1A] pt-4 space-y-3">
            {navLinks.filter(link => !link.auth || user).map((link) =>
              link.external ? (
                <a
                  key={link.to}
                  href={link.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-400 hover:text-[#00FF00] rounded-lg hover:bg-[#1A1A1A]"
                  data-testid="nav-link-shop-mobile"
                >
                  <ShoppingBag className="w-4 h-4" />
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-slate-400 hover:text-[#00FF00] rounded-lg hover:bg-[#1A1A1A]"
                >
                  {link.label}
                </Link>
              )
            )}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-slate-400 hover:text-[#00FF00] rounded-lg hover:bg-[#1A1A1A]">
                  {t('nav.my_bookings')}
                </Link>
                <Link to="/profile" onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm font-medium text-slate-400 hover:text-[#00FF00] rounded-lg hover:bg-[#1A1A1A]">
                  Mon profil
                </Link>
                {(user.role === 'super_admin' || user.role === 'franchise_admin') && (
                  <Link to="/admin" onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 text-sm font-medium text-slate-400 hover:text-[#00FF00] rounded-lg hover:bg-[#1A1A1A]">
                    {t('nav.admin')}
                  </Link>
                )}
                <button onClick={() => { logout(); navigate('/'); setMobileOpen(false); }}
                  className="block w-full text-left px-3 py-2 text-sm font-medium text-red-400 rounded-lg hover:bg-[#1A1A1A]">
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Button onClick={() => { navigate('/auth'); setMobileOpen(false); }}
                className="w-full btn-pill bg-[#00FF00] text-black font-bold">
                {t('nav.login')}
              </Button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};
